import { CreateKlus } from "@personeel/components/CreateKlus/CreateKlus.tsx";
import { useAddOrModifyKlus } from "@personeel/hooks/useFetchData.ts";
import { buildKlusModel, CreateKlusModel } from "@personeel/models/CreateKlusModel.ts";
import { useKlusSelectedKlusStore } from "@personeel/stores/SelectedKlusStore.ts";
import { useMessageBannerStore } from "@shared/shared-zustand/MessageBannerStore";
import React, { RefObject, useCallback, useEffect, useRef } from "react";
import { SubToolbarGroup } from "./Toolbar/SubToolbarGroup";
import { Toolbar } from "./Toolbar/Toolbar";
import { UrenToolBar as UrenToolbar } from "./Toolbar/UrenToolbar";
import styles from "./Werklijnen.module.css";
import { WerklijnenContainer } from "./WerklijnenContainer/WerklijnenContainer";

export const Werklijnen: React.FC = () => {
  const werklijnenRef = useRef<HTMLDivElement>(null);
  const zoom = 12;

  interface OverlayMixinElement extends HTMLElement {
    toggleContainer(open: boolean): void;
  }

  interface SidePanelToggleEvent extends Event {
    newState?: "open" | "closed";
  }

  const sidepanelRef = useRef<OverlayMixinElement>(null);
  const initialRunScenario = useRef(true);

  const setSidepanelRef = (sidepanelElement: OverlayMixinElement | null) => {
    sidepanelRef.current = sidepanelElement;
    // initieel moet het sidepanel gesloten zijn
    if (initialRunScenario.current && sidepanelElement) {
      sidepanelElement.toggleContainer(false);
      initialRunScenario.current = false;
    }
  };

  const selectedKlus = useKlusSelectedKlusStore((state) => state.selectedKlus);
  const setSelectedKlus = useKlusSelectedKlusStore((s) => s.setSelectedKlus);
  const dragOffsetMinutes = useKlusSelectedKlusStore((s) => s.dragOffsetMinutes);
  const isOpenMessageBar = useMessageBannerStore((store) => store.open);

  // Tijdens slepen van klus worden de tijden bijgewerkt in geopende sidepanel, bij klussen die niet
  // gesleept kunnen worden wordt de geselecteerde klus getoond
  let displayKlus = new CreateKlusModel();
  if (selectedKlus) {
    if (dragOffsetMinutes === 0) {
      displayKlus = new CreateKlusModel(selectedKlus);
    } else {
      displayKlus = new CreateKlusModel({
        ...selectedKlus,
        beginDatumtijd: selectedKlus.beginDatumtijd.plus({ minutes: dragOffsetMinutes }),
        eindDatumtijd: selectedKlus.eindDatumtijd.plus({ minutes: dragOffsetMinutes }),
      });
    }
  }

  const handleCloseSidepanel = useCallback(() => {
    setSelectedKlus(null);
  }, [setSelectedKlus]);

  // Klik buiten een klus in de werklijnen-container wist de omkadering én sluit het sidepanel.
  // Klikken in de sidepanel valt buiten werklijnenRef en heeft geen effect.
  useEffect(() => {
    const werklijnenElement = werklijnenRef.current;
    if (!werklijnenElement) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Als niet geklikt op een klus-button (of kind hiervan): omkadering wissen én sidepanel sluiten
      if (!target.closest('[id^="ScrollableTypes.Klus"]')) {
        setSelectedKlus(null);
      }
    };
    werklijnenElement.addEventListener("mousedown", onMouseDown);
    return () => werklijnenElement.removeEventListener("mousedown", onMouseDown);
  }, [setSelectedKlus]);

  useEffect(() => {
    if (!sidepanelRef.current) return;

    if (selectedKlus) {
      sidepanelRef.current.toggleContainer(true);
    } else {
      sidepanelRef.current.toggleContainer(false);
    }
  }, [selectedKlus]);

  const { mutate: addOrModifyKlus } = useAddOrModifyKlus();

  useEffect(() => {
    const sidepanelElement = sidepanelRef.current;

    if (!sidepanelElement) {
      return;
    }

    const handleToggle = (event: Event) => {
      const sidePanelToggleEvent = event as SidePanelToggleEvent;

      if (sidePanelToggleEvent.newState === "closed") {
        handleCloseSidepanel();
      }
    };

    sidepanelElement.addEventListener("toggle", handleToggle);

    return () => {
      sidepanelElement.removeEventListener("toggle", handleToggle);
    };
  }, [handleCloseSidepanel]);

  const addKlus = (createKlus: CreateKlusModel) => {
    const klusToBeUpdatedOrCreated = buildKlusModel(createKlus);
    addOrModifyKlus(klusToBeUpdatedOrCreated);
    sidepanelRef?.current?.toggleContainer(false);
  };

  return (
    <tractie-layout orientation="horizontal" column-gap="none" inset="none">
      <tractie-layout-slot colspan={2} inset="none">
        <div className={styles["scroll-container"]}>
          <div
            className={styles["retcontainer"] + " theme-light"}
            style={{ height: "calc(100vh - " + (isOpenMessageBar ? 188 : 140) + "px)" }}
          >
            <div
              ref={werklijnenRef}
              className={styles["right-div"]}
              id="right-div"
              data-testid="werklijnen-container"
            >
              <WerklijnenContainer scale={zoom} />
            </div>
          </div>
          <Toolbar>
            <SubToolbarGroup>
              <UrenToolbar
                scale={zoom}
                werklijnenRef={werklijnenRef as RefObject<HTMLDivElement>}
              />
            </SubToolbarGroup>
          </Toolbar>
        </div>
      </tractie-layout-slot>
      <tractie-layout-slot slot="end" orientation="horizontal">
        <tractie-side-panel
          ref={setSidepanelRef}
          dismissible
          hidden // Hide by default
          className={"sidepanel"}
        >
          <CreateKlus
            addKlus={addKlus}
            existingKlus={displayKlus}
            cancel={() => {
              handleCloseSidepanel();
            }}
          />
        </tractie-side-panel>
      </tractie-layout-slot>
    </tractie-layout>
  );
};
